import React, { useEffect } from 'react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { RadioRow, SSNInput, US_STATES } from './_shared';
import PhoneField from '../../../components/PhoneField';
import CountrySelect from '../../../components/CountrySelect';
import useGeoCountry from '../../../hooks/useGeoCountry';

export default function ContactDetailsStep({ formData, updateFormData }) {
  const set = (patch) => updateFormData({ ...formData, ...patch });
  const { countryName: geoCountryName, countryCode: geoCountryCode } = useGeoCountry();

  // Smart default: when the client lands and no country has been chosen yet,
  // pre-select whatever ipapi.co returned. Clients outside the US skip the
  // long dropdown entirely; US clients see their expected default anyway.
  useEffect(() => {
    if (!formData.country && geoCountryName) {
      set({ country: geoCountryName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoCountryName]);

  const country = formData.country || geoCountryName || 'United States';
  const isUS = country === 'United States';
  const phoneDefault = geoCountryCode || 'US';
  return (
    <div data-testid="contact-step">
      <h2 className="text-2xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Contact Details
      </h2>
      <p className="text-sm text-[#475569] mb-6">Fields marked <span className="text-red-600">*</span> are required to submit.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="firstName">Legal First Name <span className="text-red-600">*</span></Label>
          <Input id="firstName" required value={formData.firstName || ''} onChange={(e) => set({ firstName: e.target.value })} className="mt-2" data-testid="first-name-input" />
        </div>
        <div>
          <Label htmlFor="middleName">Middle Name</Label>
          <Input id="middleName" value={formData.middleName || ''} onChange={(e) => set({ middleName: e.target.value })} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="lastName">Legal Last Name <span className="text-red-600">*</span></Label>
          <Input id="lastName" required value={formData.lastName || ''} onChange={(e) => set({ lastName: e.target.value })} className="mt-2" data-testid="last-name-input" />
        </div>
        <div>
          <Label>Gender</Label>
          <RadioRow name="gender" value={formData.gender} onChange={(v) => set({ gender: v })} options={[{ label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }]} testIdPrefix="gender" />
        </div>
        <div>
          <Label htmlFor="ssn">SSN</Label>
          <SSNInput id="ssn" value={formData.ssn} onChange={(e) => set({ ssn: e.target.value })} className="mt-2" label="client Social Security Number" testId="client-ssn-input" />
        </div>
        <div>
          <Label>U.S. Citizen</Label>
          <RadioRow name="usCitizen" value={formData.usCitizen} onChange={(v) => set({ usCitizen: v })} options={[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]} testIdPrefix="uscitizen" />
        </div>
        <div className="md:col-span-2">
          <Label>Marital Status</Label>
          <RadioRow name="maritalStatus" value={formData.maritalStatus} onChange={(v) => set({ maritalStatus: v })} options={[
            { label: 'Single', value: 'single' },
            { label: 'Married', value: 'married' },
            { label: 'Divorced', value: 'divorced' },
            { label: 'Separated', value: 'separated' },
            { label: 'Widowed', value: 'widowed' },
          ]} />
        </div>
        <div>
          <Label htmlFor="birthdate">Birthdate</Label>
          <Input id="birthdate" type="date" value={formData.birthdate || ''} onChange={(e) => set({ birthdate: e.target.value })} className="mt-2" data-testid="birthdate-input" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={formData.email || ''} onChange={(e) => set({ email: e.target.value })} className="mt-2" data-testid="email-input" />
        </div>
        <div>
          <Label htmlFor="mobilePhone">Mobile Phone</Label>
          <div className="mt-2"><PhoneField testId="mobile-phone-input" defaultCountry={phoneDefault} value={formData.mobilePhone} onChange={(v) => set({ mobilePhone: v })} /></div>
        </div>
        <div>
          <Label htmlFor="homePhone">Home Phone</Label>
          <div className="mt-2"><PhoneField testId="home-phone-input" defaultCountry={phoneDefault} value={formData.homePhone} onChange={(v) => set({ homePhone: v })} /></div>
        </div>
        <div>
          <Label htmlFor="workPhone">Work Phone</Label>
          <div className="mt-2"><PhoneField testId="work-phone-input" defaultCountry={phoneDefault} value={formData.workPhone} onChange={(v) => set({ workPhone: v })} /></div>
        </div>
        <div>
          <Label htmlFor="fax">Fax</Label>
          <Input id="fax" value={formData.fax || ''} onChange={(e) => set({ fax: e.target.value })} className="mt-2" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="trustAccount">Trust or Corporate Account (Corporate/LLC)</Label>
          <Input id="trustAccount" value={formData.trustAccount || ''} onChange={(e) => set({ trustAccount: e.target.value })} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="taxId">Tax ID</Label>
          <Input id="taxId" value={formData.taxId || ''} onChange={(e) => set({ taxId: e.target.value })} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="poa">POA (If Applicable)</Label>
          <Input id="poa" value={formData.poa || ''} onChange={(e) => set({ poa: e.target.value })} className="mt-2" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="checkWriting">Check Writing Ability on Account</Label>
          <Input id="checkWriting" value={formData.checkWriting || ''} onChange={(e) => set({ checkWriting: e.target.value })} className="mt-2" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="country">Country</Label>
          <div className="mt-2">
            <CountrySelect testId="country-select-trigger" value={country} onChange={(v) => set({ country: v, state: '' })} />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="address">Address (Line 1)</Label>
          <Input id="address" value={formData.address || ''} onChange={(e) => set({ address: e.target.value })} className="mt-2" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="address2">Address (Line 2)</Label>
          <Input id="address2" value={formData.address2 || ''} onChange={(e) => set({ address2: e.target.value })} className="mt-2" placeholder="Apt, suite, etc. (optional)" />
        </div>
        <div>
          <Label htmlFor="city">City</Label>
          <Input id="city" value={formData.city || ''} onChange={(e) => set({ city: e.target.value })} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="state">{isUS ? 'State' : 'State / Province / Region'}</Label>
          {isUS ? (
            <Select value={formData.state || ''} onValueChange={(v) => set({ state: v })}>
              <SelectTrigger className="mt-2" data-testid="state-select-trigger"><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent className="max-h-60">
                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input id="state" value={formData.state || ''} onChange={(e) => set({ state: e.target.value })} className="mt-2" placeholder="e.g. Ontario, Bavaria, NSW" data-testid="state-input" />
          )}
        </div>
        <div>
          <Label htmlFor="zip">{isUS ? 'Zip Code' : 'Postal Code'}</Label>
          <Input id="zip" value={formData.zip || ''} onChange={(e) => set({ zip: e.target.value })} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="cpa">CPA</Label>
          <Input id="cpa" value={formData.cpa || ''} onChange={(e) => set({ cpa: e.target.value })} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="attorney">Attorney</Label>
          <Input id="attorney" value={formData.attorney || ''} onChange={(e) => set({ attorney: e.target.value })} className="mt-2" />
        </div>
      </div>
    </div>
  );
}
